import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR, describeAxiosError, downloadXlsx } from "@/lib/api";
import { Search, Plus, Mail, Phone, X, Loader2, Trash2, FileSpreadsheet, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Employees() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "hr_manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [company, setCompany] = useState("");
  const [tab, setTab] = useState("active");  // "active" | "ex"
  const [departments, setDepartments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [exiting, setExiting] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (dept) params.department_id = dept;
    if (company) params.company = company;
    params.status = tab === "ex" ? "left" : "active";
    const { data } = await api.get("/employees", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, dept, company, tab]);

  const remove = async (emp, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!window.confirm(`Delete ${emp.full_name} (${emp.employee_code}) from the directory?`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      load();
    } catch (err) {
      // 409 = has history. Re-confirm with the user, then force-delete.
      if (err.response?.status === 409) {
        const ok = window.confirm(
          `${err.response.data.detail}\n\nProceed anyway? Historical payroll & attendance will be kept for audit.`
        );
        if (!ok) return;
        try {
          await api.delete(`/employees/${emp.id}`, { params: { force: true } });
          load();
        } catch (err2) {
          alert(describeAxiosError(err2));
        }
      } else {
        alert(describeAxiosError(err));
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Workforce</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Employee Directory</h1>
          <p className="text-stone-600 mt-1 text-sm">{items.length} active members across departments</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadXlsx("/employees/export.xlsx", "employees_directory.xlsx",
              company ? { company } : undefined)}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: "#1B4332" }} data-testid="export-employees-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-employee-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium transition active:scale-95"
                  style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-stone-200">
        <button onClick={() => setTab("active")} data-testid="emp-tab-active"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === "active" ? "border-primary text-primary" : "border-transparent text-stone-500 hover:text-stone-800"}`}
                style={tab === "active" ? { borderColor: "var(--color-primary)", color: "var(--color-primary)" } : {}}>
          Active Employees
        </button>
        <button onClick={() => setTab("ex")} data-testid="emp-tab-ex"
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === "ex" ? "border-primary text-primary" : "border-transparent text-stone-500 hover:text-stone-800"}`}
                style={tab === "ex" ? { borderColor: "#9A3412", color: "#9A3412" } : {}}>
          Ex-Employees
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white rounded-md border border-stone-200 px-3 py-2 flex-1 min-w-[250px]">
          <Search className="w-4 h-4 text-stone-500" />
          <input data-testid="employee-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name, code, email, designation…"
                 className="bg-transparent outline-none text-sm flex-1" />
        </div>
        <select value={company} onChange={(e) => setCompany(e.target.value)} data-testid="employee-company-filter"
                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium">
          <option value="">All Companies</option>
          <option value="NAVARATNAA">Navaratnaa</option>
          <option value="SHERATAAN">Sherataan</option>
        </select>
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="employee-dept-filter"
                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white">
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employee-grid">
          {items.map((e) => (
            <Link key={e.id} to={`/employees/${e.id}`} data-testid={`employee-card-${e.employee_code}`}
                  className="card-flat block hover:border-primary/40 transition group relative">
              {canManage && tab === "active" && (
                <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setExiting(e); }}
                          title="Mark employee as exited (preserves history, stops future payroll & attendance)"
                          data-testid={`exit-emp-${e.id}`}
                          className="p-1.5 rounded-md hover:bg-orange-50 text-orange-800 border border-transparent hover:border-orange-200 bg-white">
                    <LogOut className="w-4 h-4" />
                  </button>
                  <button onClick={(ev) => remove(e, ev)} title="Delete employee (removes from directory)"
                          data-testid={`delete-emp-${e.id}`}
                          className="p-1.5 rounded-md hover:bg-red-50 text-red-700 bg-white">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-start gap-4">
                <img src={e.photo_url} alt={e.full_name} className="w-14 h-14 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs tracking-wider text-stone-500 font-semibold">{e.employee_code}</div>
                  <div className="font-heading text-lg font-semibold truncate mt-0.5 group-hover:text-primary transition">{e.full_name}</div>
                  <div className="text-sm text-stone-600 truncate">{e.designation}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-stone-600 truncate"><Mail className="w-3 h-3 shrink-0" />{e.email}</div>
                <div className="flex items-center gap-1.5 text-stone-600 truncate"><Phone className="w-3 h-3 shrink-0" />{e.mobile}</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="status-pill" style={e.company === "SHERATAAN" ? { background: "#DCE9F0", color: "#1F4863" } : { background: "var(--color-primary-light)", color: "var(--color-primary)" }}>
                  {e.company === "SHERATAAN" ? "Sherataan" : "Navaratnaa"}
                </span>
                <span className="status-pill" style={{ background: "#F4F1DE", color: "#57534E" }}>{e.grade}</span>
                <span className="text-xs text-stone-500 ml-auto">{formatINR(e.ctc_annual)} p.a.</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showAdd && <AddEmployeeModal departments={departments} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {exiting && <ExitEmployeeModal employee={exiting} onClose={() => setExiting(null)} onSaved={() => { setExiting(null); load(); }} />}
    </div>
  );
}

function AddEmployeeModal({ departments, onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", mobile: "",
    department_id: departments[0]?.id || "", designation: "", gross: "",
    company: "NAVARATNAA",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const dept = departments.find((d) => d.id === form.department_id);
      const gross = Number(form.gross) || 0;
      await api.post("/employees", {
        ...form,
        department_name: dept?.name,
        gross,
        ctc_annual: gross * 12,
      }, { timeout: 30000 });
      onCreated();
    } catch (err) {
      setError(err.code === "ECONNABORTED"
        ? "The request timed out. Check the employee directory before trying again to avoid a duplicate employee."
        : describeAxiosError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="add-employee-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="add-employee-heading" data-testid="add-employee-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 id="add-employee-heading" className="font-heading text-xl font-bold" data-testid="add-employee-heading">Add Employee</h2>
          <button onClick={onClose} disabled={busy} aria-label="Close add employee" data-testid="modal-close-button" className="p-1 hover:bg-stone-100 rounded disabled:opacity-50"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="add-employee-form">
          <fieldset disabled={busy} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="add-employee-fields">
            <Field label="First Name" testid="first-name">
              <input required data-testid="add-employee-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="form-input" />
            </Field>
            <Field label="Last Name" testid="last-name">
              <input required data-testid="add-employee-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="form-input" />
            </Field>
            <Field label="Email" testid="email">
              <input required data-testid="add-employee-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-input" />
            </Field>
            <Field label="Mobile" testid="mobile">
              <input data-testid="add-employee-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="form-input" />
            </Field>
            <Field label="Department" testid="department">
              <select data-testid="add-employee-department" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="form-input">
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Designation" testid="designation">
              <input data-testid="add-employee-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="form-input" />
            </Field>
            <Field label="Company" testid="company">
              <select data-testid="add-employee-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="form-input">
                <option value="NAVARATNAA">Navaratnaa</option>
                <option value="SHERATAAN">Sherataan</option>
              </select>
            </Field>
            <Field label="Monthly Gross (₹)" testid="gross">
              <input data-testid="add-employee-gross" type="number" min="0" value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} className="form-input" placeholder="e.g. 22000" />
              <p className="text-xs text-stone-500 mt-1" data-testid="add-employee-salary-split">Auto-splits into Basic 50% · HRA 20% · Conv ₹800 · Med ₹1,250 · Special (remainder)</p>
            </Field>
          </fieldset>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={busy} data-testid="add-employee-cancel" className="flex-1 py-2 rounded-md border border-stone-300 hover:bg-stone-50 text-sm font-medium disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-employee-button"
                    className="flex-1 py-2 rounded-md text-white text-sm font-medium" style={{ background: "var(--color-primary)" }}>
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
          {error && (
            <div role="alert" className="text-sm px-3 py-2 rounded-md mt-2" style={{ background: "#FADED7", color: "#9A3412" }} data-testid="add-employee-error">
              {error}
            </div>
          )}
        </form>
        <style>{`.form-input{width:100%;padding:8px 12px;border:1px solid #E5E5E5;border-radius:6px;font-size:14px;outline:none;transition:border-color .15s} .form-input:focus{border-color:#1B4332}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <div className="kpi-label mb-1.5">{label}</div>
      {children}
    </label>
  );
}


function ExitEmployeeModal({ employee, onClose, onSaved }) {
  const [form, setForm] = useState({
    exit_date: new Date().toISOString().slice(0, 10),
    exit_reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true); setErr("");
    try {
      await api.put(`/employees/${employee.id}`, {
        status: "left",
        exit_date: form.exit_date,
        exit_reason: form.exit_reason,
      });
      onSaved();
    } catch (e) {
      setErr(describeAxiosError(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="exit-emp-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">Mark Exit · {employee.full_name}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-stone-600 p-3 rounded-md" style={{ background: "#FEFAE0" }}>
            {employee.employee_code} · {employee.designation}. Employee will be moved to <b>Left</b> status. All attendance up to & including <b>{form.exit_date}</b> and payroll up to <b>{(form.exit_date || "").slice(0, 7)}</b> stay intact. Future rows will be removed and no new ones will be auto-generated.
          </div>
          <label className="block">
            <div className="kpi-label mb-1.5">Exit / Last Working Date</div>
            <input type="date" required value={form.exit_date} onChange={(e) => setForm({ ...form, exit_date: e.target.value })}
                   data-testid="exit-emp-date"
                   className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </label>
          <label className="block">
            <div className="kpi-label mb-1.5">Reason for leaving</div>
            <textarea rows="3" value={form.exit_reason} onChange={(e) => setForm({ ...form, exit_reason: e.target.value })}
                      placeholder="e.g. Resigned, absconding, contract ended…" data-testid="exit-emp-reason"
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </label>
          {err && <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">{err}</div>}
          <button type="submit" disabled={busy} data-testid="confirm-exit-emp-button"
                  className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "#9A3412" }}>
            {busy ? "Saving…" : "Confirm Exit"}
          </button>
        </form>
      </div>
    </div>
  );
}
